import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api";
import { downloadPdfFromMarkdown } from "../utils/fileHelpers";
import { useRunNotifier } from "../contexts/RunNotifierContext";
import { useTrial } from "../contexts/TrialContext";
import Spinner from "../shared/shared-component/Spinner";
import {
  targetInputKind,
  composeTargetValue,
  decomposeTargetValue,
  defaultDomainForInput,
  domainRoleLabel,
  selectionTargetsMainDomain,
} from "../utils/targetInputs";

import {
  parseScores,
  parsePriorities,
  parseFindings,
} from "../utils/reportParser";
import SimpleMarkdown from "../utils/SimpleMarkdown";

// Extract the structured report from Claude's full stdout output.
// Claude often prepends/appends conversational text; the actual report
// starts at the first markdown heading.
function extractReport(stdout) {
  if (!stdout) return "";
  const lines = stdout.split("\n");
  const firstHeading = lines.findIndex((l) => /^#{1,6}\s/.test(l));
  if (firstHeading === -1) return stdout.trim();
  return lines.slice(firstHeading).join("\n").trim();
}

// Turn an input key into a readable label, e.g. "targetUrl" → "Target Url".
function prettyLabel(key) {
  return key
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Build a markdown header (agent name + the parameters the user entered, e.g.
// the target URL) prepended to the terminal-output report so the downloaded
// PDF is clearly attributed and shows exactly what was analyzed.
function buildReportHeader(agentName, params, subcommand) {
  const lines = [`# ${agentName}`, ""];
  const entries = Object.entries(params || {}).filter(
    ([, v]) => v != null && String(v).trim() !== "",
  );
  if (subcommand) lines.push(`**Analysis:** ${prettyLabel(subcommand)}  `);
  for (const [k, v] of entries) {
    lines.push(`**${prettyLabel(k)}:** ${String(v)}  `);
  }
  lines.push("", "---", "");
  return lines.join("\n");
}

// Strip extension and format as readable title
function displayName(filename) {
  const dotIdx = filename.lastIndexOf(".");
  const name = dotIdx > 0 ? filename.slice(0, dotIdx) : filename;
  return name
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function FileIcon({ type }) {
  if (type === "pdf") return <span>📄</span>;
  if (type === "json") return <span>📊</span>;
  if (type === "csv") return <span>📈</span>;
  // md renders as PDF, so show PDF icon
  return <span>📄</span>;
}

function ReportBody({
  agent,
  turn,
  md,
  raw,
  heading,
  logs,
  onViewFile,
  activeFilename,
  loading,
  error,
}) {
  const target =
    turn.params?.url || turn.params?.domain || turn.params?.target || "";
  const { scores, overall } = parseScores(md);
  return (
    <div className="wrapper__report__border">
      <div className="wrapper__reportos__head mb__40 custom__row justify-content-between">
        <div className="wrapper__reportos__head__left">
          <p className="mb__16 fb__12 fw__700 ff__jetBrainsMono fc__5F636A uppercase">
            SAFFRONOS . REPORT
          </p>
          <h2 className="h3 fc__0E1218 fw__700 mb__14">
            {heading || agent.name}
          </h2>
          {target && (
            <p className="fb__16 fw__500 fc__5F636A">Target: {target}</p>
          )}
        </div>

        {overall && (
          <div className="wrapper__reportos__head__right">
            <p className="mb__16 fb__12 fw__700 ff__jetBrainsMono fc__5F636A uppercase">
              OVERALL
            </p>
            <h2 className="h3 fc__E57600 fw__700 mb__14">{overall}</h2>
            <p className="fb__16 fw__500 fc__5F636A">/ 100</p>
          </div>
        )}
      </div>

      {scores.length > 0 && (
        <div className="report__score__grid  custom__row mb__40">
          {scores.map((s) => (
            <div className="report__score__col" key={s.label}>
              <div className="report__score__col__inner">
                <p className="fb__14 fw__500 fc__5F636A uppercase ff__jetBrainsMono mb__8">
                  {s.label}
                </p>
                <h2 className="fb__24 fw__700 fc__0E1218">{s.value}</h2>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="report__md">
        {loading ? (
          <Spinner size="small" text="Loading report…" />
        ) : error ? (
          <p className="fb__12 fc__5F636A">{error}</p>
        ) : raw ? (
          <pre className="report__raw">{raw}</pre>
        ) : (
          <SimpleMarkdown md={md} />
        )}
      </div>

      {/* NEW: findings + priorities cards */}
      {/* <HistorySummary md={md} logs={logs} showScores={false} /> */}

      {/* {turn.files?.length > 0 && (
        <div className="report__files">
          <p className="fb__14 fw__600 fc__5F636A uppercase mb__8">
            Generated Files
          </p>
          <div className="wrapper__generated__files d-flex flex-wrap gap__8">
            {turn.files.map((file) => (
              <button
                key={file.filename}
                className={`common__btn common__org__btn in-flex${
                  activeFilename === file.filename ? " is__active" : ""
                }`}
                onClick={() => onViewFile(file)}
                title={`View ${displayName(file.filename)}`}
              >
                <FileIcon type={file.type} /> {displayName(file.filename)}{" "}
                <span className="d__fileType">{file.type.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {(!turn.files || turn.files.length === 0) && (
        <div className="noGeneratedFiles text__center mb__20">
          <p className="fb__14 fc__5F636A">
            No extra files were generated for this run.
          </p>
        </div>
      )}

      <div className="wrapper__demo__output">
        <p className="fb__12 fc__5F636A">
          Demo output generated for {agent.name}. Numbers are synthesized for
          preview — not live data.
        </p>
      </div> */}

      <div className="report__footer fb__12 fc__5F636A d-flex align-items-center">
        <span className="img">
          <img src="/svg/check.svg" alt="check" />
        </span>{" "}
        Saved to execution history · run {turn.executionId}
      </div>
    </div>
  );
}

// list of priorities and findings
function ListCard({ title, items }) {
  if (!items.length) return null;
  return (
    <div className="wrapper__findings__card mb__16">
      <p className="fb__12 fw__700 fc__5F636A uppercase mb__12">{title}</p>
      <ol className="history__priorities">
        {items.map((item, i) => {
          const m = item.match(/^\((CRITICAL|HIGH|MEDIUM|LOW)\)\s*(.*)/i);
          return (
            <li key={i} className="fb__14 fc__0E1218">
              {m && (
                <span className={`sev sev--${m[1].toLowerCase()}`}>{m[1]}</span>
              )}{" "}
              {m ? m[2] : item}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

//Score breakdown + priorities + findings (history runs)
function HistorySummary({ md, logs = [], showScores = true }) {
  const { scores, overall } = parseScores(md);
  const findings = parseFindings(md).slice(0, 4);
  const priorities = parsePriorities(md).slice(0, 3);

  // "Overall health: 60/100" is printed in the terminal logs, use it as a backup
  const logOverall = logs
    .map(
      (l) =>
        String(l.content || "").match(
          /overall[^0-9]{0,15}(\d{1,3})\s*\/\s*100/i,
        )?.[1],
    )
    .find(Boolean);
  const total = overall || logOverall; // real totals only, nothing calculated

  const showScoreCard = showScores && scores.length > 0;
  if (!showScoreCard && !findings.length && !priorities.length) return null;

  return (
    <>
      {showScoreCard && (
        <div className="wrapper__history__card mb__16">
          <div className="d-flex justify-content-between mb__12">
            <span className="fb__12 fw__700 fc__5F636A uppercase">
              Score breakdown
            </span>
            {total && (
              <span className="fb__14 fw__600 ff__jetBrainsMono fc__0E1218">
                {total}/100
              </span>
            )}
          </div>
          {scores.map((s) => (
            <div
              key={s.label}
              className="history__score__row d-flex justify-content-between mb__8"
            >
              <div className="">
                <span className="fb__13 fc__5F636A">{s.label}</span>
                {s.driver && (
                  <span className="fb__12 fc__5F636A">{s.driver}</span>
                )}
              </div>

              <span className="fb__13 fw__700 ff__jetBrainsMono fc__0E1218">
                {s.value}
                {s.unchecked && (
                  <span className="fb__10 fc__5F636A"> (not checked)</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {findings.map((s, i) => (
        <ListCard key={"f" + i + s.title} title={s.title} items={s.items} />
      ))}
      {priorities.map((s, i) => (
        <ListCard key={"p" + i + s.title} title={s.title} items={s.items} />
      ))}
    </>
  );
}

function UserBubble({ params, subcommand }) {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  return (
    <div className="agent__website d-flex flex-end mb__16">
      <div className="agent__website__url">
        {subcommand && (
          <div className="fb__10 fc__5F636A uppercase">
            Command: {subcommand}
          </div>
        )}
        {entries.map(([k, v]) => (
          <div className="websiteUrl__key fb__13 fc__5F636A" key={k}>
            <span className="fw__500 fb__13 fc__5F636A captalize">{k}: </span>
            <span className="fw__500 fb__13 fc__0E1218">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const ANALYSIS_PHRASES = [
  "Connecting the dots into one clear answer…",
  "Making sure the final response is crisp…",
  "Sifting through the signals to find what matters…",
  "Cross-referencing everything before we surface insights…",
  "Turning raw data into something actually useful…",
  "Reading between the lines so you don't have to…",
  "Assembling the full picture from a hundred small pieces…",
  "Asking the hard questions the data isn't answering yet…",
  "Separating the noise from the signal…",
  "Pressure-testing the findings before sharing them…",
  "Pulling the thread on a few interesting patterns…",
  "Mapping the competitive landscape in real time…",
  "Chasing down a couple of leads that look promising…",
  "Verifying the numbers add up before we move on…",
  "Doing the deep work so the summary feels effortless…",
  "Stitching together context from multiple sources…",
  "Letting curiosity drive this one for a moment…",
  "Running a final sanity check on the output…",
  "Making sense of what the algorithm is actually rewarding…",
  "Translating technical signals into plain-English actions…",
  "Hunting for the insight that changes the whole picture…",
  "Surfacing the gaps competitors haven't noticed yet…",
  "Thinking three steps ahead on this one…",
  "Checking every angle before committing to a direction…",
  "Polishing the rough edges before it reaches you…",
  "Following where the data leads, not where we assumed…",
  "Stress-testing the hypothesis with fresh evidence…",
  "Finding the pattern hiding inside the pattern…",
  "Quantifying what most people are still guessing at…",
  "Distilling hours of research into a few sharp lines…",
  "Lining up the evidence so the recommendation is obvious…",
  "Narrowing a thousand data points down to what counts…",
  "Connecting search intent to what your audience actually wants…",
  "Mapping the fastest path from here to measurable results…",
  "Peeling back the layers on this one…",
  "Looking at what's working for the best-performing pages…",
  "Reconciling what Google says with what users do…",
  "Isolating the variables that move the needle…",
  "Double-checking the logic before we commit…",
  "Scoring every factor so you know exactly where to focus…",
  "Running this through one more lens before we wrap…",
  "Putting the final answer through a quality filter…",
  "Letting the data have the last word…",
  "Almost there — tightening up the last few threads…",
  "Synthesizing a week's worth of signals into one brief…",
  "Building the clearest possible case from the evidence…",
  "Calibrating confidence before we call it done…",
  "Tracing the root cause, not just the symptom…",
  "Fun fact: Google processes over 8.5 billion searches every single day — that's about 99,000 per second.",
  "Rounding the corner on this analysis…",
  "Locking in the most actionable version of this insight…",
  "Zeroing in on what will actually move your rankings…",
  "Fun fact: The first website ever published is still live — info.cern.ch — and it went online in 1991.",
  "Making the complex feel simple — almost there…",
  "Queuing up the most important finding first…",
  "Resolving the last few ambiguities in the data…",
  "Turning correlation into something you can actually act on…",
  "Fun fact: Over 90% of web pages get zero organic traffic from Google — the gap between page 1 and page 2 is enormous.",
  "Wrapping the analysis with a tight bow…",
];

function AgentBubble({
  agent,
  turn,
  selectedDomain,
  onDownloadFile,
  onDownloadPdf,
}) {
  const [showFullOutput, setShowFullOutput] = useState(false);
  const [showRelaxMessage, setShowRelaxMessage] = useState(false);
  const [showReport, setShowReport] = useState(false);
  // Which report the side panel is showing: the terminal/stdout report, or one
  // of the files the run generated. Fetched file contents are cached by
  // filename so switching back and forth doesn't re-download.
  const [reportView, setReportView] = useState({ kind: "stdout" });
  const [fileContents, setFileContents] = useState({});
  const [fileState, setFileState] = useState({});
  const [analysisPhrase, setAnalysisPhrase] = useState(
    () => ANALYSIS_PHRASES[Math.floor(Math.random() * ANALYSIS_PHRASES.length)],
  );
  const logsEndRef = useRef(null);
  const phraseIndexRef = useRef(new Set());
  const reportPanelRef = useRef(null);

  const isQueued = turn.status === "queued";
  const isRunning = turn.status === "running";
  const isLive = isQueued || isRunning;
  const isCompleted = turn.status === "completed";
  const isFailed = turn.status === "failed";

  const duration =
    turn.startedAt && turn.completedAt
      ? Math.round(
          (new Date(turn.completedAt) - new Date(turn.startedAt)) / 1000,
        )
      : null;

  const reportMd = extractReport(turn.result?.stdout);

  const openStdoutReport = () => {
    setReportView({ kind: "stdout" });
    setShowReport(true);
  };

  // Open a generated file in the same side panel. Markdown/text files are
  // rendered; tabular/structured ones fall back to raw text so the user still
  // sees them. Binary files (PDF) can't be rendered — those still download.
  const openFileReport = async (file) => {
    if (file.type === "pdf") {
      onDownloadFile(turn.executionId, file.filename, file.type);
      return;
    }
    setReportView({ kind: "file", file });
    setShowReport(true);
    if (fileContents[file.filename] != null) return;
    setFileState((prev) => ({ ...prev, [file.filename]: "loading" }));
    try {
      const content = await api.getExecutionFileContent(
        turn.executionId,
        file.filename,
      );
      setFileContents((prev) => ({
        ...prev,
        [file.filename]:
          typeof content === "string"
            ? content
            : JSON.stringify(content, null, 2),
      }));
      setFileState((prev) => ({ ...prev, [file.filename]: "ready" }));
    } catch (err) {
      console.error("Failed to load report file:", err);
      setFileState((prev) => ({ ...prev, [file.filename]: "error" }));
    }
  };

  const viewedFile = reportView.kind === "file" ? reportView.file : null;
  const viewedFileContent = viewedFile
    ? fileContents[viewedFile.filename]
    : null;
  const viewedFileState = viewedFile ? fileState[viewedFile.filename] : "ready";
  // .md / .txt render as a report; .json / .csv stay as raw text.
  const viewedAsMarkdown =
    !viewedFile || viewedFile.type === "md" || viewedFile.type === "txt";
  const panelMd = viewedFile
    ? viewedAsMarkdown
      ? viewedFileContent || ""
      : ""
    : reportMd;
  const panelRaw =
    viewedFile && !viewedAsMarkdown ? viewedFileContent || "" : null;
  const panelHeading = viewedFile
    ? displayName(viewedFile.filename)
    : agent.name;
  // handleDownloadFile renders .md as a PDF; every other type downloads as-is.
  const panelDownloadLabel =
    !viewedFile || viewedFile.type === "md"
      ? "PDF"
      : viewedFile.type.toUpperCase();
  const canDownloadPanel = viewedFile
    ? viewedFileState === "ready"
    : Boolean(turn.result?.stdout);

  const downloadCurrentReport = () => {
    if (viewedFile) {
      onDownloadFile(turn.executionId, viewedFile.filename, viewedFile.type);
    } else {
      onDownloadPdf(turn);
    }
  };

  useEffect(() => {
    if (isLive) {
      logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [turn.logs.length, isLive]);

  useEffect(() => {
    if (!isRunning) return;
    phraseIndexRef.current = new Set();
    let timeoutId;
    const cycle = () => {
      const available = ANALYSIS_PHRASES.reduce((acc, _, i) => {
        if (!phraseIndexRef.current.has(i)) acc.push(i);
        return acc;
      }, []);
      const pool =
        available.length > 0
          ? available
          : (() => {
              phraseIndexRef.current = new Set();
              return ANALYSIS_PHRASES.map((_, i) => i);
            })();
      const next = pool[Math.floor(Math.random() * pool.length)];
      phraseIndexRef.current.add(next);
      setAnalysisPhrase(ANALYSIS_PHRASES[next]);
      const delay = 5000 + Math.random() * 5000;
      timeoutId = setTimeout(cycle, delay);
    };
    const initial = setTimeout(cycle, 5000 + Math.random() * 5000);
    return () => {
      clearTimeout(initial);
      clearTimeout(timeoutId);
    };
  }, [isRunning]);

  useEffect(() => {
    if (!isLive) return;
    const timer = setTimeout(() => setShowRelaxMessage(true), 20000);
    return () => clearTimeout(timer);
  }, [isLive]);

  // Clicking anywhere outside the report panel (or pressing Escape) closes it.
  // Buttons that open a report carry data-report-trigger so their own click
  // isn't read as an outside click, which would close and immediately reopen.
  useEffect(() => {
    if (!showReport) return;
    const onPointerDown = (e) => {
      if (reportPanelRef.current?.contains(e.target)) return;
      if (e.target.closest?.("[data-report-trigger]")) return;
      setShowReport(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowReport(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showReport]);

  const terminalLogs = turn.logs.filter(
    (log) => log.type === "stdout" || log.type === "error",
  );
  const visibleLogs = isLive
    ? terminalLogs
    : showFullOutput
      ? terminalLogs
      : terminalLogs.slice(-60);

  return (
    <div className="wrapper__execution__screen__div d-flex">
      <div className="runTech">AI</div>

      <div
        style={{ flex: 1, minWidth: 0 }}
        className="wrapper__execution__screen__inner"
      >
        {/* Status line */}
        <div className="run__keywords d-flex align-items-center gap__8 mb__10 fb__12">
          {isQueued && (
            <span className="Run__in__Queue runStatus">
              <Spinner size="small" text="Queued..." />
            </span>
          )}
          {isRunning && (
            <span className="run__In__running runStatus">
              <span
                className="pulse-dot"
                style={{ width: "7px", height: "7px" }}
              />
              {analysisPhrase}
            </span>
          )}
          {isCompleted && (
            <div className="d-flex align-items-center gap__8">
              <span className="run__Completed runStatus dots">Completed</span>
              <span className="duration fb__12 fw__700 ff__jetBrainsMono fc__5F636A ">
                {duration
                  ? ` · ${Math.floor(duration / 60)}m  ${duration % 60}s`
                  : ""}
              </span>
            </div>
          )}
          {isFailed && (
            <span className="run__Failed runStatus">
              ✕ Failed{turn.error ? `: ${turn.error}` : ""}
            </span>
          )}
        </div>

        {/* Sit-back-and-relax hint after 20 s — only when notifications are enabled */}
        {isLive &&
          showRelaxMessage &&
          "Notification" in window &&
          Notification.permission === "granted" && (
            <div className="notify__activated">
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0, color: "var(--teal-bright)" }}
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span>
                Your analysis is underway. Go ahead and explore other areas of
                the platform while we do the heavy lifting. We'll notify you as
                soon as it's ready.
              </span>
            </div>
          )}

        {/* Terminal / log output */}
        {terminalLogs.length > 0 && (
          <div
            className="wrapper__screen__map"
            style={{
              maxHeight: isLive ? "620px" : showFullOutput ? "900px" : "380px",
            }}
          >
            <div className="wrapper__screen__map__header d-flex gap__20 align-items-center">
              <div className="screen__static__controls d-flex align-items-center gap__8">
                <span className="controls red"></span>
                <span className="controls orange"></span>
                <span className="controls green"></span>
              </div>
              <div className="screen__run__data d-flex align-items-center">
                <span className="data__project__name">
                  {turn.domain} {"."}
                </span>
                <span className="data__agent__name">{agent.name}</span>
                <span>Run</span>
              </div>
            </div>
            {visibleLogs.map((log, i) => (
              <div
                className="wrapper__visibleLogs"
                key={i}
                style={{
                  color:
                    log.type === "error"
                      ? "#f87171"
                      : log.type === "info"
                        ? "#64748b"
                        : "#cbd5e1",
                  lineHeight: "1.55",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                <span className="visible__log__content">{log.content}</span>
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        )}

        {/* Expand/collapse for large log sets */}
        {isCompleted && terminalLogs.length > 60 && (
          <button
            onClick={() => setShowFullOutput((v) => !v)}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-muted)",
              fontSize: "11px",
              cursor: "pointer",
              marginBottom: "10px",
              padding: "0",
            }}
          >
            {showFullOutput
              ? "▲ Show less"
              : `▼ Show all ${terminalLogs.length} lines`}
          </button>
        )}

        {/* NEW: score + priorities cards, history runs only */}
        {isCompleted && <HistorySummary md={reportMd} logs={terminalLogs} />}

        {isCompleted && (
          <div className="wrapper__generated__files mb__20">
            {/* Generated files  isCompleted && turn.files && turn.files.length > 0 && */}
            {turn.files && turn.files.length > 0 && (
              <div className="d__files__div">
                <p className="fb__14 fw__600 uppercase fc__5F636A mb__8">
                  Generated Files
                </p>
                <div className="download__file__btn__groups d-flex align-items-center gap__8 flex-wrap">
                  {turn.files.map((file) => (
                    <button
                      className={`common__btn common__org__btn in-flex${
                        showReport && viewedFile?.filename === file.filename
                          ? " is__active"
                          : ""
                      }`}
                      key={file.filename}
                      data-report-trigger
                      onClick={() => openFileReport(file)}
                      title={`View ${displayName(file.filename)}`}
                    >
                      <FileIcon type={file.type} />
                      {displayName(file.filename)}
                      <span className="d__fileType">
                        {file.type.toUpperCase()}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Opens the stdout (claude logs) report in the side panel */}
            {turn.result?.stdout && (
              <button
                className="download__pdf__stdoutContent d-flex align-items-center gap__5 fb__13 fw__600 fc__5F636A"
                data-report-trigger
                onClick={openStdoutReport}
              >
                <span className="Img">
                  <img src="/svg/download_gray.svg" alt="View report" />
                </span>{" "}
                Download the above report
              </button>
            )}
          </div>
        )}

        {/* hidden for history (/history) runs */}
        {/* {isCompleted && !turn.fromHistory && (
          <div className="wrapper__report__section d-flex justify-content-between align-items-center">
            <div className="left__content d-flex align-items-center gap__5">
              <span className="img">
                <img src="/svg/check.svg" alt="check" />
              </span>
              {agent.name} finished — report ready.
            </div>
            <div className="right__content">
              <button
                className="common__btn common__org__btn in-flex"
                data-report-trigger
                onClick={openStdoutReport}
              >
                <img src="/svg/doc.svg" alt="doc download" />
                View report
              </button>
            </div>
          </div>
        )} */}
        {/* report model popup */}
        {showReport && (
          <div className="report__modal" ref={reportPanelRef}>
            <div className="report__modal__inner">
              <div className="report__head d-flex align-items-center justify-content-between">
                <span className="report__title d-flex align-items-center gap__5 fb__16 fw__700 fc__0E1218">
                  <img src="/svg/doc-orange.svg" alt="Pdf doc" />
                  {panelHeading} . Report
                </span>
                <div className="report__actions d-flex gap__16 align-items-center">
                  {canDownloadPanel && (
                    <button
                      className="download__pdf fb__14 fw__600 uppercase fc__0E1218"
                      onClick={downloadCurrentReport}
                    >
                      <img src="/svg/download.svg" alt="Download report" />{" "}
                      {panelDownloadLabel}
                    </button>
                  )}
                  <button
                    className="report__close close__btn"
                    title="Dismiss"
                    onClick={() => setShowReport(false)}
                  >
                    <img src="/svg/close.svg" alt="close" />
                  </button>
                </div>
              </div>
              <div className="report__model__body">
                <ReportBody
                  agent={agent}
                  turn={turn}
                  md={panelMd}
                  raw={panelRaw}
                  heading={panelHeading}
                  loading={viewedFileState === "loading"}
                  error={
                    viewedFileState === "error"
                      ? "Could not load this report file."
                      : null
                  }
                  onViewFile={openFileReport}
                  activeFilename={viewedFile?.filename}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── AI disclaimer ────────────────────────────────────────── */}
        {(isCompleted || isFailed) && (
          <div className="wrapper__ai__disclaimer">
            <p className="fb__12 fc__5F636A d-flex gap__5">
              <span style={{ flexShrink: 0, fontSize: "12px", opacity: 0.75 }}>
                ⚠️
              </span>
              <span className="">
                <strong className="fw__600 fc__ECAA0B ">
                  AI-generated content.
                </strong>{" "}
                Results are produced by large language models and may contain
                errors, omissions, or inaccuracies. Always verify important
                findings independently before making business or editorial
                decisions. SaffronOS accepts no liability for actions taken on
                this output.
              </span>
            </p>
          </div>
        )}
        {/* ── End AI disclaimer ────────────────────────────────────── */}
      </div>
    </div>
  );
}

// Maps agent → subcommand → which credential is required.
// "googleCredentials" = service account JSON (GSC / Tier-1 commands)
// "googleApiKey"      = API key (Tier-0 commands: PageSpeed, CrUX, YouTube, NLP…)
const CREDENTIAL_REQUIREMENTS = {
  // "_agent" applies when the agent has no subcommands (agent-level requirement).
  "seo-dropped-pages": {
    _agent: "googleCredentials",
  },
  "seo-google": {
    gsc: "googleCredentials",
    inspect: "googleCredentials",
    "inspect-batch": "googleCredentials",
    sitemaps: "googleCredentials",
    index: "googleCredentials",
    "index-batch": "googleCredentials",
    ga4: "googleCredentials",
    "ga4-pages": "googleCredentials",
    pagespeed: "googleApiKey",
    crux: "googleApiKey",
    "crux-history": "googleApiKey",
    youtube: "googleApiKey",
    "youtube-video": "googleApiKey",
    nlp: "googleApiKey",
    entities: "googleApiKey",
    entity: "googleApiKey",
    safety: "googleApiKey",
  },
};

const CREDENTIAL_META = {
  googleCredentials: {
    label: "Google Service Account",
    hint: "Upload your service account JSON in project settings to use GSC data.",
  },
  googleApiKey: {
    label: "Google API Key",
    hint: "Add a Google API Key in project settings to use PageSpeed, CrUX, YouTube and NLP commands.",
  },
};

const FIELD_LABEL = {
  fontSize: "11px",
  color: "var(--text-muted)",
  display: "block",
  marginBottom: "4px",
};

const FIELD_INPUT = {
  fontSize: "13px",
  padding: "10px 12px",
};

/**
 * Domain-constrained replacement for a free-text target field.
 *
 * The user picks one of the project's domains instead of typing a URL, so a run
 * can never be pointed at a site outside the project. `kind` (see
 * utils/targetInputs) decides the shape:
 *
 *   url        → domain dropdown + optional page path
 *   domain     → domain dropdown only
 *   domainList → checkboxes, submitted comma-separated
 *   property   → domain dropdown + Search Console property format
 *
 * The whole composed string is what lives in `value`, so the parent form and
 * the request payload stay exactly as they were before.
 */
function TargetField({
  input,
  kind,
  project,
  value,
  onChange,
  disabled,
  error,
}) {
  const options = project?.allowedDomains || [];
  const label = input.label || prettyLabel(input.name);

  // ── Multi-select (comma-separated hostnames) ──────────────────────────────
  if (kind === "domainList") {
    const selected = String(value || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const toggle = (domain) => {
      const next = selected.includes(domain)
        ? selected.filter((d) => d !== domain)
        : [...options.filter((d) => selected.includes(d) || d === domain)];
      onChange(next.join(","));
    };

    return (
      <div className="wrapper__input__col">
        <label className="form__label">{label}</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            padding: "7px 8px",
            border: `1px solid ${error ? "rgba(239,68,68,0.5)" : "var(--border)"}`,
            borderRadius: "8px",
            background: "rgba(255,255,255,0.04)",
          }}
        >
          {options.map((domain) => {
            const active = selected.includes(domain);
            return (
              <button
                key={domain}
                type="button"
                onClick={() => toggle(domain)}
                disabled={disabled}
                title={domainRoleLabel(domain, project)}
                style={{
                  padding: "4px 10px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: active ? "700" : "500",
                  cursor: disabled ? "not-allowed" : "pointer",
                  border: active
                    ? "1px solid rgba(6,182,212,0.5)"
                    : "1px solid var(--border)",
                  background: active
                    ? "rgba(6,182,212,0.12)"
                    : "rgba(255,255,255,0.03)",
                  color: active ? "var(--teal-bright)" : "var(--text-muted)",
                }}
              >
                {active ? "✓ " : ""}
                {domain}
              </button>
            );
          })}
        </div>
        {error && (
          <div style={{ color: "#f87171", fontSize: "11px", marginTop: "3px" }}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // ── Single domain (optionally with a path or a property format) ───────────
  const { domain: selectedDomain, path } = decomposeTargetValue(value);
  const propertyStyle = String(value || "").startsWith("sc-domain:")
    ? "sc-domain"
    : "url";

  const emit = (nextDomain, nextPath, nextStyle) =>
    onChange(
      composeTargetValue(kind, nextDomain, {
        path: nextPath,
        propertyStyle: nextStyle,
      }),
    );

  return (
    <div className="wrapper__input__col">
      <label className="form__label">{label}</label>
      <div className="wrapper__doamin__list custom__row">
        <div className="wrapper__input__col">
          <select
            value={selectedDomain}
            onChange={(e) => emit(e.target.value, path, propertyStyle)}
            disabled={disabled}
            required={input.required}
            className="form__input form__select"
            style={{
              ...FIELD_INPUT,
              flex: kind === "url" ? "1 1 60%" : "1 1 100%",
              minWidth: 0,
              borderColor: error ? "rgba(239,68,68,0.5)" : undefined,
            }}
          >
            <option value="">— Select a domain —</option>
            {options.map((domain) => {
              const role = domainRoleLabel(domain, project);
              return (
                <option key={domain} value={domain}>
                  {domain}
                  {role ? ` — ${role}` : ""}
                </option>
              );
            })}
          </select>
        </div>

        {kind === "url" && (
          <div className="wrapper__input__col">
            <input
              type="text"
              value={path}
              onChange={(e) =>
                emit(selectedDomain, e.target.value, propertyStyle)
              }
              placeholder="/page (optional)"
              disabled={disabled || !selectedDomain}
              style={{ ...FIELD_INPUT, flex: "1 1 40%", minWidth: 0 }}
              className="form__input"
            />
          </div>
        )}

        {kind === "property" && (
          <div className="wrapper__input__col">
            <select
              value={propertyStyle}
              onChange={(e) => emit(selectedDomain, path, e.target.value)}
              disabled={disabled || !selectedDomain}
              style={{ ...FIELD_INPUT, flex: "0 0 140px" }}
              title="How this property is registered in Search Console"
              className="form__input form__select"
            >
              <option value="url">URL prefix</option>
              <option value="sc-domain">Domain property</option>
            </select>
          </div>
        )}
      </div>

      <div className="domain__details fb__10 mt__4 fc__5F636A">
        {error ? (
          <span className="domain__error fc__E52F0F fb__10">{error}</span>
        ) : selectedDomain ? (
          <>
            Runs against <code>{value}</code>
          </>
        ) : (
          "Limited to this project's domains"
        )}
      </div>
    </div>
  );
}

function ChatInputForm({
  agent,
  onSubmit,
  disabled,
  isFirst,
  project,
  unrestrictedTargets,
}) {
  const [params, setParams] = useState({});
  const [subcommand, setSubcommand] = useState("");
  const [errors, setErrors] = useState({});

  // Determine if the selected subcommand requires a credential that isn't
  // configured on the current project. Returns null when everything is fine.
  const credentialWarning = (() => {
    const agentReqs = CREDENTIAL_REQUIREMENTS[agent.id];
    if (!agentReqs) return null;
    // Subcommand-level requirement, or an agent-level ("_agent") requirement for
    // agents without subcommands.
    const requiredType = subcommand ? agentReqs[subcommand] : agentReqs._agent;
    if (!requiredType) return null;
    const meta = CREDENTIAL_META[requiredType];
    if (
      requiredType === "googleCredentials" &&
      !project?.hasGoogleCredentials
    ) {
      return { requiredType, ...meta };
    }
    if (requiredType === "googleApiKey" && !project?.hasGoogleApiKey) {
      return { requiredType, ...meta };
    }
    return null;
  })();

  const getInputs = () => {
    if (agent.hasSubcommands && subcommand) {
      const sub = agent.subcommands?.find((s) => s.id === subcommand);
      return sub?.inputs || [];
    }
    return agent.inputs || [];
  };

  const handleChange = (name, value) => {
    setParams((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: "" }));
  };

  // Pre-select the project's main domain on every domain-constrained field, so
  // the common case (analyse my own site) is one click instead of two.
  //
  // Skipped for internal staff with free URL entry. They audit unrelated sites,
  // so a box arriving pre-filled with the selected project's domain is a value
  // to clear before every run — which is exactly what "can't enter URLs freely"
  // looks like from the outside, even though the field is editable.
  //
  // The reset-after-submit path below already honoured the flag; only this
  // initial seed missed it, so the two disagreed.
  const mainDomain = project?.domain || null;
  const currentInputs = getInputs();
  const inputSignature = currentInputs.map((i) => i.name).join("|");

  // The project can now be switched from the header without leaving the chat.
  // Domain-constrained fields are still holding the previous project's
  // hostname, which is not one of the new project's options, so clear them and
  // let the seeding effect below refill from the project that is now in scope.
  // Everything the user typed by hand (keywords, page paths on free-text
  // fields, the subcommand) is left alone.
  const seededProjectRef = useRef(project?.id || null);
  useEffect(() => {
    const id = project?.id || null;
    if (seededProjectRef.current === id) return;
    seededProjectRef.current = id;
    setParams((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const name of Object.keys(prev)) {
        if (targetInputKind(name) && next[name]) {
          delete next[name];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [project?.id]);

  useEffect(() => {
    if (!mainDomain || unrestrictedTargets) return;
    setParams((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const input of currentInputs) {
        const kind = targetInputKind(input.name);
        if (!kind || next[input.name]) continue;
        const seed = defaultDomainForInput(input.name, project);
        if (!seed) continue;
        next[input.name] = composeTargetValue(kind, seed, {
          propertyStyle: "url",
        });
        changed = true;
      }
      return changed ? next : prev;
    });
    // Keyed on inputSignature rather than currentInputs, which is a fresh array
    // on every render.
  }, [inputSignature, mainDomain, unrestrictedTargets]); // eslint-disable-line

  const contextApplies = selectionTargetsMainDomain(
    currentInputs,
    params,
    project,
  );
  const hasContext = !!project?.context?.trim();

  // Reasons this agent cannot be run right now, in priority order.
  //
  // Internal staff with free URL entry are never blocked on a project: they
  // can run with none selected, and with one that has no domain set, because
  // they type the target themselves. The server applies the same rule.
  const projectBlocker = unrestrictedTargets
    ? null
    : !project
      ? {
          title: "No project selected",
          hint: "Agent runs are scoped to a project's domains. Pick one on the agents page, then launch this agent again.",
          to: "/",
          cta: "Back to agents →",
        }
      : !project.allowedDomains?.length
        ? {
            title: `"${project.name}" has no main domain`,
            hint: "Add the site this project analyses before running agents against it.",
            to: `/settings?projectId=${project.id}`,
            cta: "Go to Settings →",
          }
        : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (credentialWarning || projectBlocker) return;
    const inputs = getInputs();
    const newErrors = {};
    for (const input of inputs) {
      if (input.required && !params[input.name]?.trim()) {
        newErrors[input.name] =
          !unrestrictedTargets && targetInputKind(input.name)
            ? `Select a domain for ${input.label || prettyLabel(input.name)}`
            : `${input.label} is required`;
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSubmit(params, subcommand || null);
    // Clear the form but keep the domain fields pre-selected, so a follow-up
    // run in the same chat doesn't start by re-picking the same domain.
    setParams(
      unrestrictedTargets
        ? {}
        : Object.fromEntries(
            inputs
              .map((input) => [
                input.name,
                targetInputKind(input.name),
                defaultDomainForInput(input.name, project),
              ])
              .filter(([, kind, seed]) => kind && seed)
              .map(([name, kind, seed]) => [
                name,
                composeTargetValue(kind, seed, { propertyStyle: "url" }),
              ]),
          ),
    );
  };

  const inputs = getInputs();

  return (
    <div className="wrapper__choose__agentRun">
      {isFirst && (
        <p className="fb__12 mb__14 fc__5F636A">{agent.description}</p>
      )}

      {/* {agent.id === "seo-audit" && isFirst && (
        <div
          style={{
            background: "rgba(59,130,246,0.08)",
            border: "1px solid rgba(59,130,246,0.25)",
            borderRadius: "8px",
            padding: "10px 14px",
            marginBottom: "14px",
            color: "var(--text-secondary)",
            fontSize: "12px",
          }}
        >
          <strong style={{ color: "var(--text-heading)" }}>
            Parallel Execution:
          </strong>{" "}
          Spawns 6 subagent jobs (Technical, Content, Schema, Sitemap, Geo,
          Local) running in parallel.
        </div>
      )} */}

      {/* Every run is scoped to a project's domains. Without one the server
          would reject the run, so say so up front instead of failing on submit. */}
      {projectBlocker && (
        <div
          style={{
            background: "rgba(239,68,68,0.07)",
            border: "1px solid rgba(239,68,68,0.28)",
            borderRadius: "8px",
            padding: "10px 14px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              color: "#f87171",
              fontSize: "12px",
              fontWeight: "600",
              marginBottom: "3px",
            }}
          >
            {projectBlocker.title}
          </div>
          <div
            style={{
              color: "var(--text-secondary)",
              fontSize: "12px",
              lineHeight: "1.5",
            }}
          >
            {projectBlocker.hint}
          </div>
          <Link
            to={projectBlocker.to}
            style={{
              display: "inline-block",
              marginTop: "6px",
              fontSize: "11px",
              color: "var(--accent)",
              textDecoration: "none",
              fontWeight: "600",
            }}
          >
            {projectBlocker.cta}
          </Link>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {agent.hasSubcommands && (
          <div className="wrapper__sec  mb__20">
            <label className="form__label">Command</label>
            <select
              className="form__input form__select"
              value={subcommand}
              onChange={(e) => {
                setSubcommand(e.target.value);
                setParams({});
                setErrors({});
              }}
              required
              disabled={disabled}
            >
              <option value="">— Choose a command —</option>
              {(() => {
                const subs = agent.subcommands || [];
                const hasGroups = subs.some((s) => s.group);
                if (!hasGroups) {
                  return subs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ));
                }
                const groups = [];
                const seen = new Set();
                subs.forEach((s) => {
                  if (s.group && !seen.has(s.group)) {
                    groups.push(s.group);
                    seen.add(s.group);
                  }
                });
                return groups.map((g) => (
                  <optgroup key={g} label={g}>
                    {subs
                      .filter((s) => s.group === g)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                  </optgroup>
                ));
              })()}
            </select>
            {subcommand &&
              (() => {
                const selectedSub = (agent.subcommands || []).find(
                  (s) => s.id === subcommand,
                );
                return selectedSub?.description ? (
                  <div className="fb__11 mt__5 fc__5F636A">
                    {selectedSub.description}
                  </div>
                ) : null;
              })()}
          </div>
        )}

        {/* Credential warning banner */}
        {credentialWarning && (
          <div className="wrapper__credentials__warning">
            <span className="">
              <img src="/svg/warning-icon.svg" alt="" />
            </span>
            <div>
              <p className="fb__12 fw__600 fc__E52F0F mb__4">
                {credentialWarning.label} not configured for this project
              </p>
              <p className="fb__12 fw__600 fc__5F636A mb__4">
                {credentialWarning.hint}
              </p>
              <p>
                <Link
                  to={`/settings${project?.id ? `?projectId=${project.id}` : ""}`}
                  className="link fb__12 fw__600 "
                >
                  Go to Settings →
                </Link>
              </p>
            </div>
          </div>
        )}

        {/* Project settings notice — makes it visible that extra instructions are
            being sent to the AI, and lets the user check exactly what.
            Shown when the project contributes anything: context, a specific
            target location, or both. "Global" contributes nothing. */}
        {(hasContext ||
          (project?.targetLocation && project.targetLocation !== "Global")) && (
          <details className="project__details__run">
            <summary className="summary list__none cursor fb__12 fc__5F636A">
              Using project settings from{" "}
              <strong style={{ color: "var(--teal-bright)" }}>
                {project.name}
              </strong>{" "}
              <span className="fc__0E1218 fw__600">
                —{" "}
                {[
                  project.targetLocation && project.targetLocation !== "Global"
                    ? `target ${project.targetLocation}`
                    : null,
                  hasContext
                    ? contextApplies
                      ? `context ${project.context.trim().length} chars`
                      : "context not sent (competitor run)"
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}{" "}
                · click to view
              </span>
            </summary>
            {project.targetLocation && project.targetLocation !== "Global" && (
              <p className="fb__12 fc__5F636A mt__9">
                Target location: <strong>{project.targetLocation}</strong>
              </p>
            )}
            {hasContext && !contextApplies && (
              <p className="mt__9 fb__12 fc__E52F0F">
                This run targets a competitor, so the project context below is
                not sent — it describes your own business.
              </p>
            )}
            {hasContext && (
              <p
                className={
                  contextApplies ? "hasContext" : "hasContext hasOpacity"
                }
              >
                {project.context.trim()}
              </p>
            )}
            <Link
              to={`/settings?projectId=${project.id}`}
              className="link fb__12 fw__600 mt__9"
            >
              Edit project settings →
            </Link>
          </details>
        )}

        <div className="wrapper__run__input mb__20">
          <div className="chat__form__row custom__row">
            {inputs.map((input) => {
              // Domain-constrained fields get the project dropdown instead of a
              // free-text box — see TargetField.
              //
              // Internal staff always get the plain text box, even with a
              // project selected: the whole point of the flag is to type any
              // URL, and offering a dropdown the server would not enforce is
              // worse than offering none. Guests land here too — they have no
              // project to pick domains from.
              const kind =
                !unrestrictedTargets && project?.allowedDomains?.length
                  ? targetInputKind(input.name)
                  : null;
              if (kind) {
                return (
                  <TargetField
                    key={input.name}
                    input={input}
                    kind={kind}
                    project={project}
                    value={params[input.name] || ""}
                    onChange={(v) => handleChange(input.name, v)}
                    disabled={disabled}
                    error={errors[input.name]}
                  />
                );
              }
              return (
                <div key={input.name} className="wrapper__input__col">
                  <label className="form__label">{input.label}</label>
                  {input.type === "select" ? (
                    <select
                      value={params[input.name] || ""}
                      onChange={(e) => handleChange(input.name, e.target.value)}
                      disabled={disabled}
                      required={input.required}
                      className="form__input form__select"
                    >
                      <option value="">— Select —</option>
                      {input.options?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={input.type || "text"}
                      value={params[input.name] || ""}
                      onChange={(e) => handleChange(input.name, e.target.value)}
                      placeholder={input.placeholder}
                      disabled={disabled}
                      required={input.required}
                      className="form__input"
                    />
                  )}
                  {errors[input.name] && (
                    <div
                      style={{
                        color: "#f87171",
                        fontSize: "11px",
                        marginTop: "3px",
                      }}
                    >
                      {errors[input.name]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="execution__btn">
            <button
              type="submit"
              className={
                isFirst
                  ? "common__btn common__org__btn w__100"
                  : "common__btn common__transp__btn w__100"
              }
              disabled={disabled || !!credentialWarning || !!projectBlocker}
              title={
                projectBlocker
                  ? projectBlocker.title
                  : credentialWarning
                    ? `${credentialWarning.label} required`
                    : undefined
              }
              style={{
                opacity: credentialWarning || projectBlocker ? 0.45 : undefined,
                cursor:
                  credentialWarning || projectBlocker
                    ? "not-allowed"
                    : undefined,
              }}
            >
              {disabled ? (
                <>
                  <span
                    className="spinner"
                    style={{
                      width: "12px",
                      height: "12px",
                      borderWidth: "2px",
                    }}
                  />
                  Running...
                </>
              ) : isFirst ? (
                <span>
                  <img src="/svg/run.svg" /> Run Analysis
                </span>
              ) : (
                <span>
                  <img src="/svg/refersh.svg" /> Run again
                </span>
              )}
            </button>
          </div>
        </div>
      </form>

      {/* ── Persistent AI disclaimer (always visible in the form area) ── */}
      <div className="wrapper__aiRun__warning fb__13 fw__500 d-flex">
        <span style={{ flexShrink: 0 }}>
          <img src="/svg/warning-icon-yellow.svg" alt="" />
        </span>
        <span className="fb__13 fw__500  fc__5F636A">
          AI-generated content — results may contain errors or inaccuracies.
          Verify independently before making decisions. SaffronOS accepts no
          liability for actions taken based on this output.
        </span>
      </div>
      {/* ── End persistent disclaimer ───────────────────────────────── */}
    </div>
  );
}

export default function AgentChat({
  agent,
  onBack,
  initialExecution,
  projectId,
  unrestrictedTargets = false,
  fromHistory = false,
  onRunAgain,
}) {
  const [turns, setTurns] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [project, setProject] = useState(null);
  // Keeps the input form hidden until we know the project's domains, so the
  // target dropdown never flashes as a free-text box.
  const [projectLoading, setProjectLoading] = useState(true);
  const [notifPermission, setNotifPermission] = useState(() =>
    "Notification" in window ? Notification.permission : "denied",
  );
  const [notifDismissed, setNotifDismissed] = useState(false);
  const eventSourceRef = useRef(null);
  const chatEndRef = useRef(null);
  const { registerJob, requestPermission } = useRunNotifier();
  const trial = useTrial();
  // Tracks the Claude Code session ID for the current chat chain.
  // Seeded from history on mount; updated after each completed turn.
  const sessionChainRef = useRef(null);

  // Opening a past run from history has no projectId in the URL, so fall back
  // to the project that run belonged to — otherwise a follow-up run in the
  // same chat would have no project and be rejected.
  const effectiveProjectId =
    projectId ||
    (initialExecution?.project ? String(initialExecution.project) : null);

  // Fetch the project so ChatInputForm can gate credential-dependent
  // subcommands and offer the project's domains in the target dropdown.
  useEffect(() => {
    if (!effectiveProjectId) {
      setProject(null);
      setProjectLoading(false);
      return;
    }
    setProjectLoading(true);
    api
      .getProject(effectiveProjectId)
      .then((res) => setProject(res.data.project))
      .catch(() => setProject(null))
      .finally(() => setProjectLoading(false));
  }, [effectiveProjectId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns.length]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Attach an SSE stream to an existing turn, updating it in place
  const attachStream = useCallback((jobId, turnKey) => {
    const src = api.streamExecution(
      jobId,
      (data) => {
        if (data.type === "log" && data.line) {
          setTurns((prev) =>
            prev.map((t) =>
              t.key === turnKey ? { ...t, logs: [...t.logs, data.line] } : t,
            ),
          );
        } else if (data.type === "status") {
          setTurns((prev) =>
            prev.map((t) =>
              t.key === turnKey
                ? {
                    ...t,
                    status: data.status,
                    startedAt: data.startedAt || t.startedAt,
                  }
                : t,
            ),
          );
        } else if (data.type === "complete") {
          api
            .getExecution(jobId)
            .then((res) => {
              const exec = res.data.execution;
              // Advance the session chain so the next turn in this chat continues
              // from where this one left off.
              if (exec?.claudeSessionId) {
                sessionChainRef.current = exec.claudeSessionId;
              }
              setTurns((prev) =>
                prev.map((t) =>
                  t.key === turnKey
                    ? {
                        ...t,
                        status: data.status,
                        completedAt: data.completedAt,
                        result: data.results ?? exec?.results,
                        files: exec?.files || [],
                        error: data.error || null,
                      }
                    : t,
                ),
              );
            })
            .catch(() => {
              setTurns((prev) =>
                prev.map((t) =>
                  t.key === turnKey
                    ? {
                        ...t,
                        status: data.status,
                        completedAt: data.completedAt,
                        error: data.error,
                      }
                    : t,
                ),
              );
            });
          setIsRunning(false);
          eventSourceRef.current = null;
        }
      },
      (err) => {
        console.error("SSE error:", err);
        setTurns((prev) =>
          prev.map((t) =>
            t.key === turnKey
              ? { ...t, status: "failed", error: err.message }
              : t,
          ),
        );
        setIsRunning(false);
      },
    );
    eventSourceRef.current = src;
    return src;
  }, []);

  // Hydrate from a history execution on mount
  useEffect(() => {
    if (!initialExecution) return;
    api
      .getExecution(initialExecution.id)
      .then((res) => {
        const exec = res.data.execution;
        // Seed the session chain so follow-up runs resume this execution's session
        if (exec.claudeSessionId) {
          sessionChainRef.current = exec.claudeSessionId;
        }
        const turnKey = Date.now();
        setTurns([
          {
            key: turnKey,
            fromHistory: true,
            params: exec.inputs || {},
            subcommand: exec.subcommand || null,
            executionId: exec.id,
            status: exec.status,
            logs: exec.logs || [],
            files: exec.files || [],
            result: exec.results,
            startedAt: exec.startedAt,
            completedAt: exec.completedAt,
            error: exec.error || null,
          },
        ]);
        if (exec.status === "running" || exec.status === "queued") {
          setIsRunning(true);
          const inp = exec.inputs || {};
          const resumeTarget =
            inp.url ||
            inp.domain ||
            inp.website ||
            inp.target ||
            inp.site ||
            null;
          registerJob(exec.id, agent.name, resumeTarget, agent.id);
          attachStream(exec.id, turnKey);
        }
      })
      .catch((err) => console.error("Failed to load execution:", err));
  }, [initialExecution?.id]); // attachStream is stable (useCallback with no deps)

  const handleSubmit = useCallback(
    async (params, subcommand) => {
      if (isRunning) return;
      // Block new executions once the trial has expired — re-open the popup so
      // the user can request an extension. History/downloads remain available.
      if (trial && !trial.requireActive()) return;
      let response;
      try {
        response = await api.executeAgent(
          agent.id,
          params,
          subcommand,
          sessionChainRef.current,
          effectiveProjectId || null,
        );
      } catch (err) {
        console.error("Failed to start execution:", err);
        if (err.response?.data?.code === "TRIAL_EXPIRED" && trial) {
          trial.openModal();
        } else {
          toast.error(
            err.response?.data?.error || "Failed to start the analysis.",
          );
        }
        return;
      }
      const jobId = response.data.jobId;
      const target =
        params.url ||
        params.domain ||
        params.website ||
        params.target ||
        params.site ||
        null;
      registerJob(jobId, agent.name, target, agent.id);
      const turnKey = Date.now();
      setTurns((prev) => [
        ...prev,
        {
          key: turnKey,
          params,
          subcommand,
          executionId: jobId,
          status: "queued",
          logs: [],
          files: [],
          result: null,
          startedAt: null,
          completedAt: null,
          error: null,
        },
      ]);
      setIsRunning(true);
      attachStream(jobId, turnKey);
    },
    [isRunning, agent.id, attachStream, effectiveProjectId, trial],
  );

  const handleDownloadFile = async (executionId, filename, type) => {
    const tid = toast.loading("Generating PDF…", {
      position: "top-center",
    });
    try {
      if (type === "md") {
        const content = await api.getExecutionFileContent(
          executionId,
          filename,
        );
        const base = filename.replace(/\.[^.]+$/, "");
        const pdfName = `SaffronOS-${base}-${new Date().toISOString().split("T")[0]}.pdf`;
        await downloadPdfFromMarkdown(content, pdfName);
        toast.update(tid, {
          render: "PDF downloaded successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        await api.downloadExecutionFile(executionId, filename);
        toast.update(tid, {
          render: "File downloaded successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("File download failed:", err);
      toast.update(tid, {
        render: "Download failed — " + (err?.message || "unknown error"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const handleDownloadPdf = async (turn) => {
    if (!turn.result?.stdout) return;
    let tid = toast.loading("Generating PDF… ", {
      position: "top-center",
    });

    try {
      const reportMarkdown =
        buildReportHeader(agent.name, turn.params, turn.subcommand) +
        extractReport(turn.result.stdout);
      await downloadPdfFromMarkdown(
        reportMarkdown,
        `SaffronOS-${agent.name.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.pdf`,
      );

      toast.update(tid, {
        render: "PDF downloaded successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (err) {
      console.error("PDF generation failed:", err);
      toast.update(tid, {
        render: "PDF generation failed — " + (err?.message || "unknown error"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  const agentName = agent.name;
  const initial = agentName
    .split(" ")
    .filter((w) => /^[a-zA-Z0-9]/.test(w))
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const agentCat = agent.categories?.length
    ? agent.categories
    : agent.category
      ? [agent.category]
      : [];
  const agentTagsShow = false;

  return (
    <div className="wrapper__agent__inner">
      {/* Header — sticky so the back button stays visible when scrolling */}

      <div className="agent__back__strip mb__24">
        <button
          onClick={onBack}
          className="wrapper__back__btn in-flex fb__13 fw__500  fc__5F636A"
        >
          <img src="/svg/back-lib.svg" alt="" /> Back to library
        </button>
      </div>
      <div className="wrapper__agent__header d-flex justify-content-between mb__24">
        <div className="wrapper__agents__nameTags d-flex gap__16">
          <div className="wrapper__agent__initials">
            <span className="username__initial agentInitials">{initial}</span>
          </div>
          <div className="wrapper__agent__names">
            <div className="agent__name d-flex align-items-center  gap__8 mb__6">
              <h2 className="h3 fw__700 fc__0E1218">{agent.name}</h2>
              <div className="agent__cat__d d-flex align-items-center gap__5">
                {agentCat.map((acat) => (
                  <span key={acat} className="agentCat fb__12 fw__700">
                    {acat}
                  </span>
                ))}
              </div>
            </div>
            {(isRunning || turns.length === 0) && (
              <div className="agent__details mb__12">
                <p className="fb__16 fw__400 fc__5F636A">{agent.description}</p>
              </div>
            )}

            {turns.length > 0 && (
              <div className="agent__exe__session w__100">
                <div className="mt__2 fb__13 fc__5F636A">
                  {turns.length} run{turns.length !== 1 ? "s" : ""} ·{" "}
                  {isRunning ? "session active" : "session archived"}
                </div>
              </div>
            )}

            {agentTagsShow && (
              <div className="agent__tags__data d-flex flex-wrap gap__8 ">
                <span className="agentTag fb__12 fc__5F636A ">
                  Citability score
                </span>
                <span className="agentTag fb__12 fc__5F636A">
                  AI crawler matrix
                </span>
                <span className="agentTag fb__12 fc__5F636A">
                  Brand-signal report
                </span>
                <span className="agentTag fb__12 fc__5F636A">
                  GEO action plan
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="wrapper__timing d-flex align-items-center gap__8">
          {fromHistory && (
            <div className="wrapper__pin in-flex gap__5" onClick={onRunAgain}>
              <img src="/svg/refersh.svg" color="currentColor" />
              <span className="fb__13 fc__5F636A ff__jetBrainsMono">
                Run again
              </span>
            </div>
          )}
          <div className="wrapper__exec__time">
            <span className="fb__13 fc__5F636A ff__jetBrainsMono ">
              {agent.estimatedTime}
            </span>
          </div>
        </div>
      </div>
      {/* <div className="agent__exe__session w__100">
        {turns.length > 0 && (
          <div className="mt__2 fb__11 fc__5F636A">
            {turns.length} run{turns.length !== 1 ? "s" : ""} ·{" "}
            {isRunning ? "session active" : "session archived"}
          </div>
        )}
      </div> */}

      <div className="wrapper__agent__new__design">
        <div className="agent__new__design__grid d-flex">
          {!fromHistory && (
            <div className="agent__exe__selection">
              <div className="wrapper__selection__inner">
                {turns.length === 0 && (
                  <div className="wrapper__configure mb__20">
                    <h2 className="fb__20 fw__700 fc__0E1218 mb__4">
                      Configure run
                    </h2>
                    <p className="fb__13 fc__5F636A">
                      Provide inputs and launch. Streams live to the right.
                    </p>
                  </div>
                )}
                {/* Input form.
                  The spinner is only for the first load, when there are no
                  domains to show yet. Switching project from the header
                  re-fetches while a project is already in scope, and
                  unmounting the form for that would throw away everything
                  typed into it — so the form stays, disabled, and its
                  target fields re-seed from the new project. */}
                {projectLoading && !project ? (
                  <div className="wrapper__load__project__run fb__12 fc__5F636A d-flex align-items-center gap__8">
                    <Spinner size="small" text="Loading project…" />
                  </div>
                ) : (
                  <ChatInputForm
                    agent={agent}
                    onDomainChange={setSelectedDomain}
                    onSubmit={handleSubmit}
                    disabled={isRunning || projectLoading}
                    isFirst={turns.length === 0}
                    project={project}
                    unrestrictedTargets={unrestrictedTargets}
                  />
                )}
              </div>
            </div>
          )}
          <div className="agent__exe__blackScreen">
            {turns.map((turn, idx) => (
              <div key={turn.key} className="wrapper__run__count ">
                <div className="run__count mt__8 fb__13 fc__5F636A text__center">
                  Run {idx + 1}
                </div>
                <UserBubble params={turn.params} subcommand={turn.subcommand} />
                <AgentBubble
                  agent={agent}
                  selectedDomain={selectedDomain}
                  turn={turn}
                  onDownloadFile={handleDownloadFile}
                  onDownloadPdf={handleDownloadPdf}
                />
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        </div>
      </div>

      {/* Notification permission prompt */}
      {notifPermission === "default" && !notifDismissed && (
        <div className="wrapper__agent__notification__window mt__24">
          <img src="/svg/notifiy-icon.svg" alt="Notification alert" />
          <div className="inner__div flex__1">
            <div className="fw__600">
              Get notified when your analysis completes
            </div>
            <div>
              These analyses can take a few minutes. Enable browser
              notifications and we'll alert you the moment any run finishes —
              even if you've switched to another tab or page.
            </div>
          </div>
          <div className="ntf__button__area d-flex align-items-center gap__8 flex-srink-0">
            <button
              className="common__btn common__org__btn"
              onClick={async () => {
                const result = await requestPermission();
                setNotifPermission(result);
              }}
            >
              Enable
            </button>
            <button
              className="close__btn"
              onClick={() => setNotifDismissed(true)}
              title="Dismiss"
            >
              <img src="/svg/close.svg" alt="close" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
